/**
 * Single source of truth for everything brand-specific in this deployment.
 *
 * The codebase is a template designed to be re-skinned for each LocalBzz
 * client. To swap brands:
 *   1. Edit the values below
 *   2. Replace `src/assets/hero-logo.png` with the new brand's logo
 *   3. Apply the schema migrations + the per-deployment brand seed migration
 *      (see supabase/migrations/*_brand_seed.sql for the editable file)
 *   4. Set the function env vars `SITE_URL`, `FROM_EMAIL_FALLBACK`,
 *      `CRON_SECRET` for the new deployment
 *
 * Anything tagged "runtime override" can also be changed by an admin via the
 * site_settings table after deploy without rebuilding.
 */
export const BRAND = {
  name: 'On The Ranch',
  tagline: 'Regenerative ranching, straight from the pasture.',

  // <head> metadata, baked into index.html at build time
  pageTitle: 'On The Ranch',
  pageDescription: 'Regenerative ranching, straight from the pasture.',
  author: 'The Provision House',

  // One-line value prop shown directly under the logo. Tells a stranger
  // what the show is in 2 seconds. Runtime override: site_settings.value_prop.
  valueProp: 'Real talk on regenerative ranching with Bart and Trent.',

  // Form copy. Runtime override: site_settings keys cta_button_text,
  // success_heading, success_subtext, signup_prompt, next_event_label,
  // value_prop.
  // signupPrompt reads naturally with or without an active event — the
  // page never has a special "no event" mode, just an inviting CTA that
  // sometimes happens to sit below a scheduled date.
  ctaButtonText: 'Count Me In',
  successHeading: "You're on the list.",
  successSubtext: "We'll be in touch before the next one.",
  signupPrompt: "Drop your email and we'll send you the link.",
  nextEventLabel: 'Join Our Virtual Event',

  // Post-registration modal copy
  postSignupTitle: "You're in!",
  postSignupQuestion: 'Got a question or topic for the show?',

  // Email defaults. fromName ships in code; fromEmail is intentionally
  // empty — admin must set it via site_settings.from_email so we never
  // accidentally ship with a stale fork-era address.
  fromName: 'On The Ranch',

  // Default meeting title used by admin tools and the Zoom create-meeting
  // edge function when no explicit title is provided.
  defaultMeetingTitle: 'On The Ranch',

  // Default `subscribers.source` value when the form caller doesn't pass one
  defaultSignupSource: 'Landing Page',
} as const;

export type Brand = typeof BRAND;
